/**
 * Chief-complaint → Provider-impression suggestion map.
 *
 * When a crew picks a chief complaint the impression is frequently
 * pre-determined by that picker alone — "Chest Pain" nearly always
 * resolves to ACS / Chest Pain Non-Cardiac / Chest Pain Etiology
 * Undetermined / or (for trauma) Chest Trauma. Forcing the crew to
 * re-search the impression picker for a known-likely mapping is a
 * textbook P9 violation ("Never Repeat the Same Data").
 *
 * Instead, this file declares the most-likely impression candidates
 * for each chief complaint. The UI surfaces them as a compact chip
 * strip below the Chief Complaint field with "Use ▸" buttons — one
 * tap applies the suggestion to Primary Impression. The crew can
 * still search manually if they want something else.
 */

/** Returns up to 3 most-likely primary impressions for a chief complaint. */
export function suggestImpressionsForComplaint(complaint: string): string[] {
  const c = complaint.toLowerCase().trim();
  if (!c) return [];

  // Longest-match-first lookup — the first entry whose trigger regex
  // matches wins. Order matters.
  for (const rule of RULES) {
    if (rule.test.test(c)) return rule.suggest;
  }
  return [];
}

interface Rule {
  test: RegExp;
  suggest: string[];
}

const RULES: Rule[] = [
  // --- Cardiovascular ---
  { test: /chest pain|chest pressure|chest tightness/,
    suggest: [
      'Acute Coronary Syndrome (ACS)',
      'Chest Pain — Etiology Undetermined',
      'Chest Pain — Non-Cardiac',
    ] },
  { test: /cardiac arrest|pulseless|cpr/,
    suggest: [
      'Cardiac Arrest — Medical',
      'Cardiac Arrest — Return of Spontaneous Circulation (ROSC)',
      'Traumatic Cardiac Arrest',
    ] },
  { test: /palpitations|racing heart/,
    suggest: [
      'Cardiac Dysrhythmia (Tachycardia — Narrow)',
      'Supraventricular Tachycardia (SVT)',
      'Atrial Fibrillation with RVR',
    ] },
  { test: /syncope|near[- ]syncope|lightheaded/,
    suggest: [
      'Syncope / Near-Syncope',
      'Hypotension / Shock — Cardiogenic',
      'Cardiac Dysrhythmia (Bradycardia)',
    ] },

  // --- Respiratory ---
  { test: /shortness of breath|dyspnea|difficulty breathing|sob\b/,
    suggest: [
      'Respiratory Distress',
      'COPD Exacerbation',
      'CHF Exacerbation',
    ] },
  { test: /wheez/,
    suggest: [
      'Asthma Exacerbation',
      'COPD Exacerbation',
      'Anaphylaxis',
    ] },
  { test: /respiratory arrest/,
    suggest: ['Respiratory Arrest', 'Respiratory Failure', 'Cardiac Arrest — Medical'] },
  { test: /choking/,
    suggest: ['Upper Airway Obstruction', 'Allergic Reaction — Systemic', 'Anaphylaxis'] },
  { test: /coughing up blood|hemoptysis/,
    suggest: ['Pulmonary Embolism (Suspected)', 'Respiratory Distress', 'Pneumonia (Suspected)'] },

  // --- Neurological ---
  { test: /stroke|cva|lvo|facial droop|slurred speech|one[- ]sided weak/,
    suggest: [
      'Stroke / CVA (Acute)',
      'Large Vessel Occlusion (LVO) Stroke',
      'Transient Ischemic Attack (TIA)',
    ] },
  { test: /severe headache.*worst/,
    suggest: [
      'Subarachnoid Hemorrhage (Suspected)',
      'Intracranial Hemorrhage (Suspected)',
      'Migraine',
    ] },
  { test: /headache|migraine/,
    suggest: ['Headache — Primary', 'Migraine', 'Hypertensive Emergency'] },
  { test: /seizure.*active/,
    suggest: ['Status Epilepticus', 'Seizure — Generalized', 'Seizure — Focal'] },
  { test: /seizure.*post|post[- ]ictal/,
    suggest: ['Post-Ictal State', 'Seizure — Generalized', 'Altered Mental Status (Cause Unknown)'] },
  { test: /seizure/,
    suggest: ['Seizure — Generalized', 'Seizure — Focal', 'Status Epilepticus'] },
  { test: /altered mental|ams|unresponsive|confusion/,
    suggest: [
      'Altered Mental Status (Cause Unknown)',
      'Hypoglycemia',
      'Stroke / CVA (Acute)',
    ] },
  { test: /dizziness|vertigo/,
    suggest: ['Vertigo — Peripheral', 'Vertigo — Central', 'Syncope / Near-Syncope'] },

  // --- GI / GU ---
  { test: /abdominal pain/,
    suggest: [
      'Abdominal Pain — Etiology Undetermined',
      'Appendicitis (Suspected)',
      'GI Bleeding (Upper)',
    ] },
  { test: /nausea|vomiting/,
    suggest: ['Nausea / Vomiting', 'Gastroenteritis', 'GI Bleeding (Upper)'] },
  { test: /vomiting blood|hematemesis/,
    suggest: ['GI Bleeding (Upper)', 'Esophageal Varices (Bleeding)'] },
  { test: /flank pain/,
    suggest: ['Renal Colic / Kidney Stone', 'Pyelonephritis', 'Abdominal Pain — Etiology Undetermined'] },
  { test: /vaginal bleeding|pregnancy/,
    suggest: ['Vaginal Bleeding (Non-OB)', 'Miscarriage / Pregnancy Loss', 'Ectopic Pregnancy (Suspected)'] },
  { test: /labor|imminent delivery/,
    suggest: ['Active Labor', 'Imminent Delivery', 'Delivery — Field'] },

  // --- Trauma ---
  { test: /motor vehicle|mvc|motorcycle|bicycle collision|pedestrian struck/,
    suggest: ['Multi-System Trauma', 'Traumatic Brain Injury — Mild', 'Cervical Spine Injury (Suspected)'] },
  { test: /fall.*ground/,
    suggest: ['Traumatic Brain Injury — Mild', 'Lower Extremity Fracture (Closed)', 'Cervical Spine Injury (Suspected)'] },
  { test: /fall.*height/,
    suggest: ['Multi-System Trauma', 'Spinal Cord Injury', 'Pelvic Fracture (Suspected)'] },
  { test: /fall/,
    suggest: ['Traumatic Brain Injury — Mild', 'Fall — Ground Level', 'Lower Extremity Fracture (Closed)'] },
  { test: /gunshot|gsw/,
    suggest: ['Chest Trauma — Penetrating', 'Abdominal Trauma — Penetrating', 'Hemorrhagic Shock'] },
  { test: /stab|stabbing/,
    suggest: ['Chest Trauma — Penetrating', 'Abdominal Trauma — Penetrating', 'Laceration — Major / Hemorrhagic'] },
  { test: /assault/,
    suggest: ['Traumatic Brain Injury — Mild', 'Facial Trauma', 'Sexual Assault'] },
  { test: /burn.*thermal/,
    suggest: ['Burn — Thermal — 2nd Degree', 'Burn — Thermal — 3rd Degree', 'Burn — Inhalation'] },
  { test: /burn.*chemical/,
    suggest: ['Burn — Chemical', 'Burn — Inhalation'] },
  { test: /burn.*electrical/,
    suggest: ['Burn — Electrical', 'Cardiac Dysrhythmia (Tachycardia — Wide)'] },

  // --- Toxic / Environmental ---
  { test: /overdose.*opioid|opioid/,
    suggest: ['Opioid Overdose', 'Respiratory Arrest', 'Altered Mental Status (Cause Unknown)'] },
  { test: /overdose.*intentional|suicide attempt/,
    suggest: ['Polysubstance Overdose', 'Suicide Attempt', 'Altered Mental Status (Cause Unknown)'] },
  { test: /overdose|ingestion|poisoning/,
    suggest: ['Polysubstance Overdose', 'Poisoning / Ingestion — Unknown Substance', 'Opioid Overdose'] },
  { test: /alcohol intoxication/,
    suggest: ['Alcohol Intoxication', 'Altered Mental Status (Cause Unknown)'] },
  { test: /carbon monoxide/,
    suggest: ['Carbon Monoxide Poisoning', 'Smoke Inhalation'] },
  { test: /hypotherm/,
    suggest: ['Hypothermia', 'Frostbite'] },
  { test: /heat/,
    suggest: ['Heat Exhaustion', 'Heat Stroke'] },
  { test: /drowning|submersion/,
    suggest: ['Drowning / Submersion Injury', 'Cardiac Arrest — Medical'] },

  // --- Behavioral ---
  { test: /suicidal|self[- ]?harm/,
    suggest: ['Suicidal Ideation', 'Suicide Attempt', 'Self-Harm'] },
  { test: /anxiety|panic/,
    suggest: ['Anxiety / Panic Attack', 'Chest Pain — Non-Cardiac'] },
  { test: /psychiatric|hallucinat/,
    suggest: ['Psychiatric Emergency — Unspecified', 'Acute Psychosis', 'Agitation / Excited Delirium'] },

  // --- Endocrine / Metabolic ---
  { test: /hypoglycem|low blood sugar/,
    suggest: ['Hypoglycemia', 'Altered Mental Status (Cause Unknown)'] },
  { test: /hyperglycem|high blood sugar|dka/,
    suggest: ['Hyperglycemia (Non-Ketotic)', 'Diabetic Ketoacidosis (DKA)', 'Hyperosmolar Hyperglycemic State (HHS)'] },

  // --- Infection / Allergy ---
  { test: /fever/,
    suggest: ['Fever of Unknown Origin', 'Sepsis', 'Pneumonia (Suspected)'] },
  { test: /sepsis/,
    suggest: ['Sepsis', 'Septic Shock'] },
  { test: /allergic reaction|anaphylax/,
    suggest: ['Anaphylaxis', 'Allergic Reaction — Systemic', 'Angioedema'] },

  // --- Pediatric ---
  { test: /pediatric respiratory/,
    suggest: ['Pediatric Respiratory Distress', 'Epiglottitis / Croup (Pediatric)', 'Asthma Exacerbation'] },
  { test: /pediatric seizure/,
    suggest: ['Pediatric Seizure', 'Status Epilepticus'] },
  { test: /pediatric fever/,
    suggest: ['Pediatric Fever', 'Pediatric Sepsis'] },

  // --- Pain / Musculoskeletal ---
  { test: /back pain/,
    suggest: ['Spinal Cord Syndrome', 'Abdominal Pain — Etiology Undetermined', 'Renal Colic / Kidney Stone'] },
  { test: /extremity (pain|injury|fracture)/,
    suggest: ['Lower Extremity Fracture (Closed)', 'Upper Extremity Fracture (Closed)', 'Sprain / Strain'] },

  // --- Other ---
  { test: /bleeding.*external/,
    suggest: ['Hemorrhagic Shock', 'Laceration — Major / Hemorrhagic', 'GI Bleeding (Upper)'] },
  { test: /weakness|malaise/,
    suggest: ['General Weakness / Malaise', 'Sepsis', 'Dehydration'] },
  { test: /lift assist/,
    suggest: ['Lift Assist — No Injury', 'No Acute Complaint'] },
  { test: /no patient|welfare check/,
    suggest: ['No Patient Contact', 'Patient Refused Assessment'] },
];
